
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useSearchParams } from "react-router-dom";
import { AuthProvider } from "@/contexts/auth";
import AuthGuard from "@/components/auth/AuthGuard";
import { lazy, Suspense, useEffect, useState } from "react";

// Eagerly-loaded — the homepage and 404 should be available without a chunk fetch
import IndexPage from "./pages/Index";
import NotFound from "./pages/NotFound";
import ComingSoonPage from "./pages/ComingSoonPage";

// Lazy-loaded routes — each becomes its own chunk, fetched only when visited.
// Major mobile perf win: ~400 KB of JS is no longer downloaded up-front.
const StoryPage             = lazy(() => import("./pages/StoryPage"));
const EpisodePage           = lazy(() => import("./pages/EpisodePage"));
const AuthorPage            = lazy(() => import("./pages/AuthorPage"));
const BrowsePage            = lazy(() => import("./pages/BrowsePage"));
const CreateStoryPage       = lazy(() => import("./pages/CreateStoryPage"));
const SubscriptionPage      = lazy(() => import("./pages/SubscriptionPage"));
const AuthorDashboardPage   = lazy(() => import("./pages/AuthorDashboardPage"));
const AdminDashboardPage    = lazy(() => import("./pages/AdminDashboardPage"));
const AdminGuidePage        = lazy(() => import("./pages/AdminGuidePage"));
const CategoriesPage        = lazy(() => import("./pages/CategoriesPage"));
const CategoryPage          = lazy(() => import("./pages/CategoryPage"));
const LibraryPage           = lazy(() => import("./pages/LibraryPage"));
const AuthorGuidelinesPage  = lazy(() => import("./pages/AuthorGuidelinesPage"));
const ContactPage           = lazy(() => import("./pages/ContactPage"));
const TermsOfServicePage    = lazy(() => import("./pages/TermsOfServicePage"));
const PrivacyPolicyPage     = lazy(() => import("./pages/PrivacyPolicyPage"));
const CookiePolicyPage      = lazy(() => import("./pages/CookiePolicyPage"));
const AboutPage             = lazy(() => import("./pages/AboutPage"));
const BlogPage              = lazy(() => import("./pages/BlogPage"));
const CareersPage           = lazy(() => import("./pages/CareersPage"));
const AuthPage              = lazy(() => import("./pages/AuthPage"));
const ProfilePage           = lazy(() => import("./pages/ProfilePage"));
const BecomeAuthorPage      = lazy(() => import("./pages/BecomeAuthorPage"));
const SettingsPage          = lazy(() => import("./pages/SettingsPage"));
const EpisodeEditorPage     = lazy(() => import("./pages/EpisodeEditorPage"));

const queryClient = new QueryClient();

// Controlled via VITE_COMING_SOON=true env variable — set in Vercel dashboard to enable
const COMING_SOON_MODE = import.meta.env.VITE_COMING_SOON === 'true';

// Bypass key — append ?access=Cv9#mXq2$Lp7@Zt to the URL to skip the Coming Soon page.
// Access persists in localStorage so you only need to use it once per browser.
// Example: https://your-site.vercel.app?access=Cv9#mXq2$Lp7@Zt
const ACCESS_KEY = "Cv9#mXq2$Lp7@Zt";

const AppContent = () => {
  const [searchParams] = useSearchParams();
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    // Check if user has access via URL param or localStorage
    const accessParam = searchParams.get('access');
    const storedAccess = localStorage.getItem('chapterly_access');
    
    if (accessParam === ACCESS_KEY) {
      localStorage.setItem('chapterly_access', 'true');
      setHasAccess(true);
    } else if (storedAccess === 'true') {
      setHasAccess(true);
    }
  }, [searchParams]);

  // Show Coming Soon page if enabled and user doesn't have access
  if (COMING_SOON_MODE && !hasAccess) {
    return <ComingSoonPage />;
  }

  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-chapterly-purple">Loading...</div>
      </div>
    }>
    <Routes>
      <Route path="/" element={<IndexPage />} />
      <Route path="/story/:storyId" element={<StoryPage />} />
      {/* Episode editor — /new must come before /:episodeId so it isn't swallowed as a param */}
      <Route path="/story/:storyId/episode/new" element={
        <AuthGuard requireAuth requireAuthor authorOnly>
          <EpisodeEditorPage />
        </AuthGuard>
      } />
      <Route path="/story/:storyId/episode/:episodeId/edit" element={
        <AuthGuard requireAuth requireAuthor authorOnly>
          <EpisodeEditorPage />
        </AuthGuard>
      } />
      <Route path="/story/:storyId/episode/:episodeId" element={<EpisodePage />} />
      <Route path="/author/:username" element={<AuthorPage />} />
      <Route path="/browse" element={<BrowsePage />} />
      <Route path="/write" element={
        <AuthGuard requireAuth requireAuthor authorOnly>
          <CreateStoryPage />
        </AuthGuard>
      } />
      <Route path="/auth" element={
        <AuthGuard requireUnauth>
          <AuthPage />
        </AuthGuard>
      } />
      <Route path="/profile" element={
        <AuthGuard requireAuth>
          <ProfilePage />
        </AuthGuard>
      } />
      {/* Route for viewing author profiles via username */}
      <Route path="/author/:username" element={<AuthorPage />} />
      <Route path="/settings" element={
        <AuthGuard requireAuth>
          <SettingsPage />
        </AuthGuard>
      } />
      <Route path="/subscription" element={<SubscriptionPage />} />
      <Route path="/become-author" element={
        <AuthGuard requireAuth>
          <BecomeAuthorPage />
        </AuthGuard>
      } />
      <Route path="/author-dashboard" element={
        <AuthGuard requireAuth requireAuthor authorOnly>
          <AuthorDashboardPage />
        </AuthGuard>
      } />
      <Route path="/admin-dashboard" element={
        <AuthGuard requireAuth requireAdmin>
          <AdminDashboardPage />
        </AuthGuard>
      } />
      <Route path="/admin-guide" element={
        <AuthGuard requireAuth requireAdmin>
          <AdminGuidePage />
        </AuthGuard>
      } />
      <Route path="/categories" element={<CategoriesPage />} />
      <Route path="/category/:slug" element={<CategoryPage />} />
      <Route path="/library" element={
        <AuthGuard requireAuth readerOnly>
          <LibraryPage />
        </AuthGuard>
      } />
      <Route path="/author-guidelines" element={<AuthorGuidelinesPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
